import { ArrowUpRight, Facebook, Instagram, Twitter, Youtube } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { Reveal } from "@/components/Reveal";
import { SectionHeading } from "@/components/SectionHeading";
import { SITE, VIDEOS } from "@/data/content";

const socials = [
  { key: "YouTube", url: SITE.youtube, Icon: Youtube, testId: "social-link-youtube" },
  { key: "Instagram", url: SITE.instagram, Icon: Instagram, testId: "social-link-instagram" },
  { key: "Facebook", url: SITE.facebook, Icon: Facebook, testId: "social-link-facebook" },
  { key: "Twitter", url: SITE.twitter, Icon: Twitter, testId: "social-link-twitter" },
].filter((s) => s.url);

export const SocialSection = () => {
  const { t } = useLang();
  return (
    <section data-testid="social-section" className="bg-ink py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-20">
          <div>
            <SectionHeading
              eyebrow={t.social.eyebrow}
              title={t.social.title}
              titleItalic={t.social.titleItalic}
              description={t.social.description}
            />
            <Reveal delay={0.2} className="mt-9 flex flex-wrap items-center gap-4">
              <a
                href={SITE.youtube}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="social-subscribe-button"
                className="flex items-center gap-2 rounded-full bg-ember px-6 py-3 text-xs font-bold uppercase tracking-wider text-ink transition-colors duration-300 hover:bg-emberHover"
              >
                <Youtube className="h-4 w-4" />
                {t.social.cta}
              </a>
              {socials.map(({ key, url, Icon, testId }) => (
                <a
                  key={key}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={testId}
                  aria-label={key}
                  className="flex h-11 w-11 items-center justify-center rounded-full border border-line text-ash transition-colors duration-300 hover:border-ember hover:text-ember"
                >
                  <Icon className="h-4 w-4" />
                </a>
              ))}
            </Reveal>
          </div>

          <Reveal delay={0.1}>
            <a
              href={VIDEOS.storiesWorthWatching.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="social-thumbnail-link"
              className="group relative block overflow-hidden rounded-md border border-line"
            >
              <img
                src={VIDEOS.storiesWorthWatching.image}
                alt={VIDEOS.storiesWorthWatching.title}
                onError={(e) => {
                  if (e.currentTarget.src !== VIDEOS.storiesWorthWatching.imageFallback) e.currentTarget.src = VIDEOS.storiesWorthWatching.imageFallback;
                }}
                loading="lazy"
                className="aspect-video w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-transparent to-transparent" />
              <div className="absolute bottom-5 right-5 flex items-center gap-2 rounded-full bg-ink/80 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-cream backdrop-blur-sm transition-colors duration-300 group-hover:bg-ember group-hover:text-ink">
                YouTube
                <ArrowUpRight className="h-3.5 w-3.5" />
              </div>
            </a>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
